(function () {
  "use strict";

  var APP_PROXY_BASE = "/apps/garden";
  var PUBLIC_ROUTE_BASE = APP_PROXY_BASE + "/public";
  var activePublicRoute = null;
  var publicRouteRequestId = 0;
  var suppressPublicRouteCloseNavigation = false;

  var _selfScript = document.currentScript;
  var _viewerReady = null;

  function _injectAsset(tag, attrs) {
    var el = document.createElement(tag);
    Object.keys(attrs).forEach(function (k) { el[k] = attrs[k]; });
    document.head.appendChild(el);
    return el;
  }

  function _waitForViewer(resolve, reject) {
    var attempts = 0;
    (function poll() {
      if (window.GardenDocumentViewer && window.GardenDocumentViewer.openSharedList) {
        resolve();
      } else if (++attempts > 50) {
        _viewerReady = null;
        reject(new Error("Shared list modal is unavailable."));
      } else {
        setTimeout(poll, 100);
      }
    })();
  }

  function ensureViewerAssets() {
    if (!_selfScript) return;
    var viewerCss = _selfScript.getAttribute("data-viewer-css");
    var documentCss = _selfScript.getAttribute("data-document-css");
    var toastJs = _selfScript.getAttribute("data-toast-js");
    var toastCss = _selfScript.getAttribute("data-toast-css");

    if (viewerCss && !document.querySelector('link[href="' + viewerCss + '"]')) {
      _injectAsset("link", { rel: "stylesheet", href: viewerCss });
    }
    if (
      documentCss &&
      !document.querySelector('link[href="' + documentCss + '"]')
    ) {
      _injectAsset("link", { rel: "stylesheet", href: documentCss });
    }
    if (toastCss && !document.querySelector('link[href="' + toastCss + '"]')) {
      _injectAsset("link", { rel: "stylesheet", href: toastCss });
    }
    if (toastJs && !document.querySelector('script[src="' + toastJs + '"]')) {
      _injectAsset("script", { src: toastJs });
    }
  }

  function ensureDocumentViewer() {
    ensureViewerAssets();
    if (window.GardenDocumentViewer && window.GardenDocumentViewer.openSharedList) {
      return Promise.resolve();
    }
    if (_viewerReady) return _viewerReady;

    var viewerJs = _selfScript && _selfScript.getAttribute("data-viewer-js");
    if (!viewerJs) {
      return Promise.reject(new Error("Shared list modal is unavailable."));
    }

    _viewerReady = new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + viewerJs + '"]')) {
        _waitForViewer(resolve, reject);
        return;
      }

      var script = _injectAsset("script", { src: viewerJs });
      script.onload = function () { _waitForViewer(resolve, reject); };
      script.onerror = function () {
        _viewerReady = null;
        reject(new Error("Failed to load shared list viewer."));
      };
    });

    return _viewerReady;
  }

  function sanitizeString(value) {
    if (typeof value === "string") {
      return value.trim();
    }
    if (value == null) {
      return "";
    }
    return String(value).trim();
  }

  function sanitizeAppProxyBase(value) {
    return sanitizeString(value).replace(/\/+$/, "");
  }

  function safeDecodeURIComponent(value) {
    try {
      return decodeURIComponent(String(value || ""));
    } catch (_) {
      return String(value || "");
    }
  }

  function isSameOriginReferrer(referrer) {
    var raw = sanitizeString(referrer);
    if (!raw) return false;
    try {
      return new URL(raw, window.location.origin).origin === window.location.origin;
    } catch (_) {
      return false;
    }
  }

  function getSharedQueryParams(source) {
    var params = source instanceof URLSearchParams
      ? source
      : new URLSearchParams(
          source instanceof URL ? source.search : window.location.search,
        );
    var handle = String(params.get("garden-shared-handle") || "").trim();
    var listId = String(params.get("garden-shared-list-id") || "").trim();
    if (
      params.get("garden-shared-list") !== "1" ||
      !handle ||
      !listId
    ) {
      return null;
    }

    return {
      handle: handle,
      listId: listId,
      role: params.get("garden-shared-role") === "editor" ? "editor" : "viewer",
      invitationId:
        String(
          params.get("garden-shared-invitation-id") ||
          params.get("garden-shared-invite-id") ||
          params.get("garden-shared-invitation") ||
          "",
        ).trim() || null,
      token: String(params.get("garden-shared-token") || "").trim() || null,
      legacyInviteToken:
        String(params.get("garden-shared-invite") || "").trim() || null,
      authIntent: String(params.get("garden-shared-auth") || "").trim(),
      autoAfterAuth: ["1", "prepare"].indexOf(
        String(params.get("garden-shared-auth") || "").trim(),
      ) !== -1,
    };
  }

  function getPublicCollectionQueryParams(source) {
    var params = source instanceof URLSearchParams
      ? source
      : new URLSearchParams(
          source instanceof URL ? source.search : window.location.search,
        );
    var handle = String(params.get("garden-public-handle") || "").trim();
    var collectionId = String(params.get("garden-public-collection-id") || "").trim();
    if (
      params.get("garden-public-collection") !== "1" ||
      !handle ||
      !collectionId
    ) {
      return null;
    }
    return { handle: handle, collectionId: collectionId };
  }

  function clearPublicCollectionQueryParams() {
    var url = new URL(window.location.href);
    [
      "garden-public-collection",
      "garden-public-handle",
      "garden-public-collection-id",
    ].forEach(function (key) {
      url.searchParams.delete(key);
    });
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatCurrency(value, currency) {
    var amount = Number(value);
    if (!Number.isFinite(amount)) return "";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: String(currency || "USD").trim() || "USD",
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return "$" + amount.toFixed(2);
    }
  }

  function cleanProductTitle(value) {
    var raw = sanitizeString(value);
    if (!raw) return "";
    return raw
      .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/i, "")
      .replace(/\s*\|\s*DOES NOT SHIP\s*$/i, "")
      .replace(/DOES NOT SHIP\s*$/i, "")
      .trim();
  }

  function normalizeRole(value) {
    return sanitizeString(value).toLowerCase() === "editor" ? "editor" : "viewer";
  }

  function parsePublicGardenRoute(source) {
    var url;
    try {
      url = source instanceof URL
        ? source
        : new URL(String(source || window.location.href), window.location.origin);
    } catch (_) {
      return null;
    }

    if (url.origin !== window.location.origin) {
      return null;
    }

    var pathname = String(url.pathname || "").replace(/\/+$/, "");
    var basePath = sanitizeAppProxyBase(PUBLIC_ROUTE_BASE);
    if (!basePath || pathname.indexOf(basePath) !== 0) {
      return null;
    }

    var suffix = pathname.slice(basePath.length);
    if (!suffix || suffix === "/") {
      return null;
    }

    var parts = suffix
      .split("/")
      .filter(Boolean)
      .map(function (segment) {
        return safeDecodeURIComponent(segment);
      });

    if (!parts.length) {
      return null;
    }

    var handle = sanitizeString(parts[0]);
    if (!handle || handle.toLowerCase() === "product") {
      return null;
    }

    var type = "plants";
    var resourceId = null;
    if (parts.length === 1) {
      type = "plants";
    } else if (parts.length === 3 && sanitizeString(parts[1]).toLowerCase() === "list") {
      type = "list";
      resourceId = sanitizeString(parts[2]);
    } else if (
      parts.length === 3 &&
      sanitizeString(parts[1]).toLowerCase() === "collection"
    ) {
      type = "collection";
      resourceId = sanitizeString(parts[2]);
    } else {
      return null;
    }

    if ((type === "list" || type === "collection") && !resourceId) {
      return null;
    }

    return {
      url: url,
      href: url.toString(),
      key: url.pathname + url.search,
      path: url.pathname + url.search + url.hash,
      handle: handle,
      type: type,
      resourceId: resourceId,
      token: sanitizeString(url.searchParams.get("token")) || null,
      role: sanitizeString(url.searchParams.get("role")) || null,
    };
  }

  function buildPublicPreviewUrl(route) {
    var path =
      sanitizeAppProxyBase(APP_PROXY_BASE) +
      "/v1/public/" +
      encodeURIComponent(sanitizeString(route && route.handle));

    if (route && route.type === "list" && route.resourceId) {
      path += "/list/" + encodeURIComponent(String(route.resourceId));
    } else if (route && route.type === "collection" && route.resourceId) {
      path += "/collection/" + encodeURIComponent(String(route.resourceId));
    }

    var url = new URL(path, window.location.origin);
    url.searchParams.set("format", "json");
    if (route && route.token) {
      url.searchParams.set("token", sanitizeString(route.token));
    }
    if (route && route.role) {
      url.searchParams.set("role", normalizeRole(route.role));
    }
    return url.toString();
  }

  function buildSharedPreviewUrl(input) {
    var url = new URL(
      APP_PROXY_BASE +
        "/v1/public/" +
        encodeURIComponent(String(input.handle || "").trim()) +
        "/list/" +
        encodeURIComponent(String(input.listId || "").trim()),
      window.location.origin,
    );
    url.searchParams.set("format", "json");
    url.searchParams.set("role", input.role === "editor" ? "editor" : "viewer");
    if (input.token) {
      url.searchParams.set("token", String(input.token).trim());
    }
    if (input.legacyInviteToken) {
      url.searchParams.set("invite", String(input.legacyInviteToken).trim());
    }
    return url.toString();
  }

  function buildReturnUrlForAuth(authIntent) {
    var url = new URL(window.location.href);
    var intent = sanitizeString(authIntent);
    if (intent) {
      url.searchParams.set("garden-shared-auth", intent);
    } else {
      url.searchParams.delete("garden-shared-auth");
    }
    return url.pathname + url.search + url.hash;
  }

  function clearSharedHostQueryParams() {
    var url = new URL(window.location.href);
    [
      "garden-shared-list",
      "garden-shared-handle",
      "garden-shared-list-id",
      "garden-shared-invitation-id",
      "garden-shared-invite-id",
      "garden-shared-invitation",
      "garden-shared-owner",
      "garden-shared-role",
      "garden-shared-token",
      "garden-shared-invite",
      "garden-shared-auth",
    ].forEach(function (key) {
      url.searchParams.delete(key);
    });
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }

  function buildAccountAuthUrl(pathname, authIntent) {
    var destination = String(pathname || "/account/login").trim() || "/account/login";
    var url = new URL(destination, window.location.origin);
    url.searchParams.set("return_url", buildReturnUrlForAuth(authIntent));
    return url.pathname + url.search + url.hash;
  }

  function setFormReturnInput(form, name, value) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function shouldPreserveReturnUrlForForm(form) {
    var formType = form.querySelector('input[name="form_type"]');
    var typeValue = formType ? sanitizeString(formType.value) : "";
    if (typeValue === "customer_login" || typeValue === "create_customer") {
      return true;
    }
    return !!form.querySelector("#customer_email, #register_email");
  }

  function syncAccountAuthReturnUrl() {
    var currentUrl = new URL(window.location.href);
    if (!/^\/account\/(?:login|register)\/?$/.test(currentUrl.pathname)) {
      return;
    }

    var returnUrl =
      sanitizeString(currentUrl.searchParams.get("return_url")) ||
      sanitizeString(currentUrl.searchParams.get("return_to"));
    if (!returnUrl) return;

    var authPaths = [
      window.Theme?.routes?.account_login_url || "/account/login",
      window.Theme?.routes?.account_register_url || "/account/register",
    ].map(function (path) {
      return new URL(path, window.location.origin).pathname.replace(/\/+$/, "");
    });

    Array.prototype.forEach.call(document.querySelectorAll("a[href]"), function (anchor) {
      try {
        var href = anchor.getAttribute("href");
        if (!href) return;
        var url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        var pathname = url.pathname.replace(/\/+$/, "");
        if (authPaths.indexOf(pathname) === -1) return;
        url.searchParams.set("return_url", returnUrl);
        anchor.setAttribute("href", url.pathname + url.search + url.hash);
      } catch (_) {
        // Ignore malformed hrefs.
      }
    });

    Array.prototype.forEach.call(document.querySelectorAll("form"), function (form) {
      if (!shouldPreserveReturnUrlForForm(form)) return;
      setFormReturnInput(form, "return_url", returnUrl);
      setFormReturnInput(form, "return_to", returnUrl);
    });
  }

  function buildSharedCopyName(defaultName) {
    var baseName = sanitizeString(defaultName) || "Shared List";
    return /\bcopy\b/i.test(baseName) ? baseName : baseName + " Copy";
  }

  var copyNameDialog = null;

  function ensureCopyNameDialog() {
    if (copyNameDialog) return copyNameDialog;
    var root = document.createElement("div");
    root.className = "my-plants-modal my-plants-modal--utility";
    root.dataset.modal = "shared-copy-name";
    root.hidden = true;
    root.innerHTML =
      '<div class="my-plants-modal__backdrop" data-action="close" aria-hidden="true"></div>' +
      '<div class="my-plants-modal__dialog my-plants-modal__dialog--utility" role="dialog" aria-modal="true" aria-labelledby="shared-copy-name-title">' +
      '<div class="my-plants-utility-dialog">' +
      '<div class="my-plants-modal__header my-plants-utility-dialog__header">' +
      '<h3 class="my-plants-modal__title" id="shared-copy-name-title">Name your list</h3>' +
      '<button type="button" class="my-plants-modal__close" data-action="close" aria-label="Close dialog">\u00d7</button>' +
      "</div>" +
      '<form class="my-plants-utility-dialog__form" data-form="copy-name">' +
      '<p class="my-plants-utility-dialog__message" data-role="message"></p>' +
      '<label class="my-plants-utility-dialog__label" data-role="label" for="shared-copy-name-input">List name</label>' +
      '<input type="text" id="shared-copy-name-input" class="my-plants-utility-dialog__input" data-role="input" maxlength="100" autocomplete="off" />' +
      '<p class="my-plants-utility-dialog__error" data-role="error" hidden></p>' +
      '<div class="my-plants-utility-dialog__actions">' +
      '<button type="button" class="my-plants-utility-dialog__btn my-plants-utility-dialog__btn--secondary" data-action="cancel">Cancel</button>' +
      '<button type="submit" class="my-plants-utility-dialog__btn" data-role="confirm">Save</button>' +
      "</div>" +
      "</form>" +
      "</div>" +
      "</div>";
    document.body.appendChild(root);

    copyNameDialog = {
      root: root,
      dialog: root.querySelector(".my-plants-modal__dialog"),
      title: root.querySelector("#shared-copy-name-title"),
      message: root.querySelector('[data-role="message"]'),
      label: root.querySelector('[data-role="label"]'),
      input: root.querySelector('[data-role="input"]'),
      error: root.querySelector('[data-role="error"]'),
      confirm: root.querySelector('[data-role="confirm"]'),
      form: root.querySelector('[data-form="copy-name"]'),
      state: null,
    };
    return copyNameDialog;
  }

  function closeCopyNameDialog(dialog, value) {
    if (!dialog || !dialog.state) return;
    var resolve = dialog.state.resolve;
    dialog.state = null;
    document.removeEventListener("keydown", dialog.keydownHandler);
    dialog.root.classList.remove("is-open");
    window.setTimeout(function () {
      if (!dialog.state) {
        dialog.root.hidden = true;
      }
    }, 200);
    if (typeof resolve === "function") {
      resolve(value);
    }
  }

  function promptForCopyName(defaultName) {
    var suggestedName = buildSharedCopyName(defaultName);
    if (typeof document === "undefined" || !document.body) {
      var fallback = window.prompt("Name your copied list.", suggestedName);
      if (fallback == null) return Promise.resolve(null);
      return Promise.resolve(sanitizeString(fallback) || suggestedName);
    }

    var dialog = ensureCopyNameDialog();
    if (dialog.state) {
      closeCopyNameDialog(dialog, null);
    }

    dialog.message.textContent =
      "Enter a name for the copy that will be saved in your garden.";
    dialog.input.value = suggestedName;
    dialog.error.hidden = true;
    dialog.error.textContent = "";

    return new Promise(function (resolve) {
      dialog.state = { resolve: resolve };

      dialog.keydownHandler = function (event) {
        if (event.key === "Escape") {
          event.preventDefault();
          closeCopyNameDialog(dialog, null);
        }
      };

      function handleClick(event) {
        var action = event.target && event.target.closest
          ? event.target.closest("[data-action]")
          : null;
        if (!action) return;
        var name = action.getAttribute("data-action");
        if (name === "close" || name === "cancel") {
          event.preventDefault();
          closeCopyNameDialog(dialog, null);
        }
      }

      function handleSubmit(event) {
        event.preventDefault();
        var value = sanitizeString(dialog.input.value) || suggestedName;
        closeCopyNameDialog(dialog, value);
      }

      dialog.root.onclick = handleClick;
      dialog.form.onsubmit = handleSubmit;
      document.addEventListener("keydown", dialog.keydownHandler);

      dialog.root.hidden = false;
      requestAnimationFrame(function () {
        dialog.root.classList.add("is-open");
      });
      window.setTimeout(function () {
        if (dialog.input && !dialog.root.hidden) {
          dialog.input.focus();
          dialog.input.setSelectionRange(
            dialog.input.value.length,
            dialog.input.value.length,
          );
        }
      }, 40);
    });
  }

  async function promptForSharedListCopyName(defaultName) {
    var suggestedName = buildSharedCopyName(defaultName);
    var answer = window.prompt("Name your copied list.", suggestedName);
    if (answer == null) return null;
    var normalized = sanitizeString(answer);
    return normalized || suggestedName;
  }

  async function createSharedListCopy(name) {
    var response = await fetch(APP_PROXY_BASE + "/v1/lists", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ name: name }),
    });

    if (!response.ok) {
      var message = "Unable to create a list right now.";
      try {
        var errorData = await response.json();
        if (errorData && (errorData.error || errorData.message)) {
          message = String(errorData.error || errorData.message);
        }
      } catch (_) {
        // Ignore JSON parsing failures.
      }
      throw new Error(message);
    }

    var payload = await response.json().catch(function () {
      return {};
    });
    var list =
      payload && payload.list && typeof payload.list === "object"
        ? payload.list
        : payload;
    var listId = sanitizeString(list && list.id);
    if (!listId) {
      throw new Error("The copied list could not be created.");
    }
    return list;
  }

  function buildSharedCopyItemPayload(item) {
    if (!item || typeof item !== "object") return null;
    var productReference =
      sanitizeString(item.productId) ||
      sanitizeString(item.productGid);
    if (!productReference) return null;

    var payload = {
      productId: productReference,
      productGid: sanitizeString(item.productGid) || null,
      productTitle: sanitizeString(item.title) || "Untitled",
      productHandle: sanitizeString(item.handle) || null,
      productVendor: sanitizeString(item.vendor) || null,
      productImage: sanitizeString(item.image) || null,
      variantId: sanitizeString(item.variantId) || null,
      title: sanitizeString(item.title) || "Untitled",
      handle: sanitizeString(item.handle) || null,
      vendor: sanitizeString(item.vendor) || null,
      image: sanitizeString(item.image) || null,
      price: resolveItemPriceValue(item),
      currency:
        sanitizeString(item.currencyCode) ||
        sanitizeString(item.currency) ||
        "USD",
      tags: Array.isArray(item.tags) ? item.tags.filter(Boolean) : [],
      notes:
        sanitizeString(item.note) ||
        sanitizeString(item.notes) ||
        null,
    };

    Object.keys(payload).forEach(function (key) {
      if (payload[key] === null || payload[key] === "") {
        delete payload[key];
      }
    });

    return payload;
  }

  async function appendSharedCopyItem(listId, item) {
    var payload = buildSharedCopyItemPayload(item);
    if (!payload) return false;

    var response = await fetch(
      APP_PROXY_BASE +
        "/v1/lists/" +
        encodeURIComponent(String(listId || "").trim()) +
        "/items",
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      var message = "Unable to copy one of these items.";
      try {
        var errorData = await response.json();
        if (errorData && (errorData.error || errorData.message)) {
          message = String(errorData.error || errorData.message);
        }
      } catch (_) {
        // Ignore JSON parsing failures.
      }
      throw new Error(message);
    }

    var responsePayload = await response.json().catch(function () {
      return {};
    });
    var copiedItem =
      responsePayload && responsePayload.item && typeof responsePayload.item === "object"
        ? responsePayload.item
        : responsePayload;
    return sanitizeString(copiedItem && copiedItem.id) || true;
  }

  async function reorderSharedCopyItems(listId, orderedItemIds) {
    var ids = (Array.isArray(orderedItemIds) ? orderedItemIds : [])
      .map(function (id) {
        return sanitizeString(id);
      })
      .filter(Boolean);
    if (!ids.length) return;

    var response = await fetch(
      APP_PROXY_BASE +
        "/v1/lists/" +
        encodeURIComponent(String(listId || "").trim()) +
        "/items",
      {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ orderedItemIds: ids }),
      },
    );

    if (!response.ok) {
      throw new Error("Unable to restore the copied list order.");
    }
  }

  async function appendSharedCopyItems(listId, items) {
    var sharedItems = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!sharedItems.length) {
      return { copiedCount: 0, copiedItemIds: [] };
    }

    var maxConcurrentCopies = Math.min(4, sharedItems.length);
    var copiedItemIds = new Array(sharedItems.length);
    var copiedCount = 0;
    var nextIndex = 0;

    async function copyNextItem() {
      while (nextIndex < sharedItems.length) {
        var itemIndex = nextIndex;
        nextIndex += 1;
        var copied = await appendSharedCopyItem(listId, sharedItems[itemIndex]);
        if (copied) {
          copiedCount += 1;
          if (typeof copied === "string") {
            copiedItemIds[itemIndex] = copied;
          }
        }
      }
    }

    var workers = [];
    for (var workerIndex = 0; workerIndex < maxConcurrentCopies; workerIndex += 1) {
      workers.push(copyNextItem());
    }
    await Promise.all(workers);

    return {
      copiedCount: copiedCount,
      copiedItemIds: copiedItemIds.filter(Boolean),
    };
  }

  async function saveSharedListCopy(sharedQuery, payload) {
    var viewer =
      payload && payload.viewer && typeof payload.viewer === "object"
        ? payload.viewer
        : {};
    if (!viewer.isSignedIn) {
      window.location.href = buildAccountAuthUrl(
        window.Theme?.routes?.account_login_url || "/account/login",
      );
      return;
    }

    var list =
      payload && payload.list && typeof payload.list === "object"
        ? payload.list
        : {};
    var listName = await promptForSharedListCopyName(list.name || "Shared List");
    if (!listName) return;

    var createdList = await createSharedListCopy(listName);
    var createdListId = sanitizeString(createdList && createdList.id);
    if (!createdListId) {
      throw new Error("The copied list could not be created.");
    }

    var sharedItems = mapSharedListItems(list.items || []);
    var copyResult = await appendSharedCopyItems(createdListId, sharedItems);
    var copiedCount = copyResult.copiedCount;
    if (
      copyResult.copiedItemIds.length > 1 &&
      copyResult.copiedItemIds.length === copiedCount
    ) {
      try {
        await reorderSharedCopyItems(createdListId, copyResult.copiedItemIds);
      } catch (error) {
        console.warn("[Shared List Host] Failed to restore copied list order:", error);
      }
    }

    showToast(
      copiedCount > 0
        ? "Saved a copy to My Garden."
        : "Created a new empty list in My Garden.",
    );
    window.location.href = "/account?garden-view=lists";
  }

  function buildCopiedListUrl(listId) {
    var base = "/account?garden-view=lists";
    var id = sanitizeString(listId);
    if (!id) return base;
    return (
      base +
      "&garden-open-shared-list=1&garden-shared-list-id=" +
      encodeURIComponent(id)
    );
  }

  function quantityLabel(count, singular, plural) {
    return count + " " + (count === 1 ? singular : plural);
  }

  async function runSharedListCopy(options) {
    options = options || {};
    var viewer =
      options.viewer && typeof options.viewer === "object"
        ? options.viewer
        : {};

    if (!viewer.isSignedIn) {
      window.location.href = buildAccountAuthUrl(
        window.Theme?.routes?.account_login_url || "/account/login",
      );
      return;
    }

    var listName = await promptForCopyName(options.defaultName || "Shared List");
    if (!listName) return;

    var viewerApi = window.GardenDocumentViewer || null;
    var items = (Array.isArray(options.items) ? options.items : []).filter(
      Boolean,
    );
    var totalItems = items.length;

    if (viewerApi && viewerApi.showCopyProgress) {
      viewerApi.showCopyProgress("Creating your list\u2026");
    }

    try {
      var createdList = await createSharedListCopy(listName);
      var createdListId = sanitizeString(createdList && createdList.id);
      if (!createdListId) {
        throw new Error("The copied list could not be created.");
      }

      if (totalItems > 0 && viewerApi && viewerApi.showCopyProgress) {
        viewerApi.showCopyProgress(
          "Copying " + quantityLabel(totalItems, "item", "items") + "\u2026",
        );
      }

      var copyResult = totalItems
        ? await appendSharedCopyItems(createdListId, items)
        : { copiedCount: 0, copiedItemIds: [] };

      if (
        copyResult.copiedItemIds.length > 1 &&
        copyResult.copiedItemIds.length === copyResult.copiedCount
      ) {
        try {
          await reorderSharedCopyItems(createdListId, copyResult.copiedItemIds);
        } catch (error) {
          console.warn(
            "[Shared List Host] Failed to restore copied list order:",
            error,
          );
        }
      }

      var copiedCount = copyResult.copiedCount;
      var successUrl = buildCopiedListUrl(createdListId);

      if (viewerApi && viewerApi.showCopySuccess) {
        viewerApi.showCopySuccess({
          title: "Saved to your garden",
          message:
            copiedCount > 0
              ? "Added " +
                quantityLabel(copiedCount, "plant", "plants") +
                " to \u201C" +
                listName +
                "\u201D."
              : "Created \u201C" + listName + "\u201D in My Garden.",
          href: successUrl,
          linkLabel: "Open your list",
        });
      } else {
        showToast(
          copiedCount > 0
            ? "Saved a copy to My Garden."
            : "Created a new empty list in My Garden.",
        );
        window.location.href = successUrl;
      }
    } catch (error) {
      var message =
        error instanceof Error && error.message
          ? error.message
          : "Please try again.";
      if (viewerApi && viewerApi.showCopyError) {
        viewerApi.showCopyError(message);
      } else {
        showToast(message);
      }
    }
  }

  function buildSharedCopyAction(options) {
    options = options || {};
    return function () {
      return runSharedListCopy(options);
    };
  }

  function buildProductUrl(item) {
    var product = item && item.product && typeof item.product === "object" ? item.product : {};
    var cached = item && item._cached && typeof item._cached === "object" ? item._cached : {};
    var explicit =
      sanitizeString(item && item.productUrl) ||
      sanitizeString(item && item.url) ||
      sanitizeString(item && item.href) ||
      sanitizeString(item && item.onlineStoreUrl) ||
      sanitizeString(product && (product.productUrl || product.url || product.href || product.onlineStoreUrl)) ||
      sanitizeString(cached && (cached.productUrl || cached.url || cached.href || cached.onlineStoreUrl));
    if (explicit) return explicit;

    var handle =
      sanitizeString(item && (item.handle || item.productHandle)) ||
      sanitizeString(product && (product.handle || product.productHandle)) ||
      sanitizeString(cached && (cached.handle || cached.productHandle));
    return handle ? "/products/" + encodeURIComponent(handle) : "";
  }

  function normalizePriceValue(value, options) {
    if (value == null) return null;

    var rawValue = value;
    if (typeof rawValue === "object") {
      if (rawValue && rawValue.amount != null) {
        rawValue = rawValue.amount;
      } else {
        return null;
      }
    }

    var rawString = typeof rawValue === "string" ? rawValue.trim() : "";
    var amount = Number(rawValue);
    if (!Number.isFinite(amount)) return null;

    if (options && options.unit === "cents") {
      return amount / 100;
    }

    var looksLikeIntegerCents =
      (typeof rawValue === "number" && Number.isInteger(rawValue) && Math.abs(rawValue) >= 1000) ||
      (rawString && /^-?\d+(?:\.0+)?$/.test(rawString) && Math.abs(amount) >= 1000);

    return looksLikeIntegerCents ? amount / 100 : amount;
  }

  function resolveItemPriceValue(item) {
    if (!item || typeof item !== "object") return null;
    if (item.priceCents != null) {
      return normalizePriceValue(item.priceCents, { unit: "cents" });
    }
    if (item.price_cents != null) {
      return normalizePriceValue(item.price_cents, { unit: "cents" });
    }
    return normalizePriceValue(
      item.priceAmount != null ? item.priceAmount : item.price,
    );
  }

  function resolveItemCompareAtPriceValue(item) {
    if (!item || typeof item !== "object") return null;
    if (item.compareAtPriceCents != null) {
      return normalizePriceValue(item.compareAtPriceCents, { unit: "cents" });
    }
    if (item.compare_at_price_cents != null) {
      return normalizePriceValue(item.compare_at_price_cents, { unit: "cents" });
    }
    return normalizePriceValue(
      item.compareAtPriceAmount != null
        ? item.compareAtPriceAmount
        : item.compareAtPrice,
    );
  }

  function formatPriceLabel(value, currency) {
    return value == null ? "" : formatCurrency(value, currency || "USD");
  }

  function normalizeImageCandidate(value) {
    if (!value) return "";
    if (typeof value === "string") return sanitizeString(value);
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i += 1) {
        var nested = normalizeImageCandidate(value[i]);
        if (nested) return nested;
      }
      return "";
    }
    if (typeof value === "object") {
      return (
        sanitizeString(value.url) ||
        sanitizeString(value.src) ||
        sanitizeString(value.originalSrc) ||
        sanitizeString(value.transformedSrc) ||
        sanitizeString(value.preview_image && value.preview_image.src) ||
        sanitizeString(value.previewImage && value.previewImage.url) ||
        sanitizeString(value.previewImage && value.previewImage.src) ||
        sanitizeString(value.featuredMedia && value.featuredMedia.url) ||
        sanitizeString(value.featuredMedia && value.featuredMedia.src) ||
        sanitizeString(value.featuredMedia && value.featuredMedia.previewImage && value.featuredMedia.previewImage.url) ||
        sanitizeString(value.featuredMedia && value.featuredMedia.previewImage && value.featuredMedia.previewImage.src) ||
        sanitizeString(value.featured_media && value.featured_media.url) ||
        sanitizeString(value.featured_media && value.featured_media.src) ||
        sanitizeString(value.featured_media && value.featured_media.preview_image && value.featured_media.preview_image.url) ||
        sanitizeString(value.featured_media && value.featured_media.preview_image && value.featured_media.preview_image.src) ||
        sanitizeString(value.media && value.media.previewImage && value.media.previewImage.url) ||
        sanitizeString(value.media && value.media.previewImage && value.media.previewImage.src) ||
        sanitizeString(value.media && value.media.preview_image && value.media.preview_image.url) ||
        sanitizeString(value.media && value.media.preview_image && value.media.preview_image.src) ||
        sanitizeString(value.image && value.image.url) ||
        sanitizeString(value.image && value.image.src) ||
        sanitizeString(value.node && value.node.image && value.node.image.url) ||
        sanitizeString(value.node && value.node.featuredImage && value.node.featuredImage.url)
      );
    }
    return "";
  }

  function resolveListItemImage(item) {
    if (!item || typeof item !== "object") return "";
    var product = item.product && typeof item.product === "object" ? item.product : {};
    var cached = item._cached && typeof item._cached === "object" ? item._cached : {};
    return (
      normalizeImageCandidate(item.image) ||
      normalizeImageCandidate(item.imageUrl) ||
      normalizeImageCandidate(item.featuredImage) ||
      normalizeImageCandidate(item.featured_image) ||
      normalizeImageCandidate(item.productImage) ||
      normalizeImageCandidate(item.product_image) ||
      normalizeImageCandidate(item.images) ||
      normalizeImageCandidate(product.featuredImage) ||
      normalizeImageCandidate(product.featured_image) ||
      normalizeImageCandidate(product.image) ||
      normalizeImageCandidate(product.images) ||
      normalizeImageCandidate(cached.image) ||
      normalizeImageCandidate(cached.imageUrl) ||
      normalizeImageCandidate(cached.featuredImage) ||
      normalizeImageCandidate(item.bestImage)
    );
  }

  function normalizeListItems(items) {
    return (Array.isArray(items) ? items : [])
      .filter(Boolean)
      .map(function (item) {
        var priceValue = resolveItemPriceValue(item);
        var compareAtValue = resolveItemCompareAtPriceValue(item);
        var currency =
          sanitizeString(item && (item.currencyCode || item.currency)) || "USD";

        return {
          id:
            sanitizeString(item && (item.id || item.itemId || item.listItemId)) ||
            sanitizeString(item && item.productId) ||
            sanitizeString(item && (item.handle || item.productHandle)),
          title:
            cleanProductTitle(
              item &&
                (item.title ||
                  item.name ||
                  item.commonName ||
                  (item._cached && item._cached.title)),
            ) || "Untitled",
          image:
            resolveListItemImage(item),
          productUrl: buildProductUrl(item),
          handle: sanitizeString(item && (item.handle || item.productHandle)),
          scientificName: sanitizeString(item && (item.species || item.latinName)),
          location: sanitizeString(item && (item.location || item.locationInGarden)),
          note: sanitizeString(item && (item.note || item.notes || item.description)),
          notes: sanitizeString(item && (item.note || item.notes || item.description)),
          vendor: sanitizeString(item && item.vendor),
          variants: sanitizeString(item && (item.variantTitle || item.variantName)),
          available:
            typeof (item && item.available) === "boolean" ? !!item.available : null,
          dateLabel: sanitizeString(item && (item.dateLabel || item.addedLabel)),
          price: priceValue,
          priceAmount: priceValue,
          compareAtPrice: compareAtValue,
          compareAtPriceAmount: compareAtValue,
          priceLabel:
            sanitizeString(item && item.priceLabel) ||
            formatPriceLabel(priceValue, currency),
          compareAtPriceLabel:
            sanitizeString(item && item.compareAtPriceLabel) ||
            formatPriceLabel(compareAtValue, currency),
          currency: currency,
          currencyCode: currency,
          tags: Array.isArray(item && item.tags) ? item.tags : [],
          variantId: sanitizeString(item && item.variantId) || null,
          productId: sanitizeString(item && item.productId) || null,
          productGid: sanitizeString(item && item.productGid) || null,
        };
      });
  }

  function extractCollectionOwnedIds(payload, collection) {
    var sources = [
      payload && payload.ownedProductIds,
      payload && payload.ownedProducts,
      payload && payload.progress && payload.progress.ownedProducts,
      payload && payload.progressDetails && payload.progressDetails.ownedProducts,
      collection && collection.ownedProducts,
      collection && collection.progress && collection.progress.ownedProducts,
      collection && collection.progressDetails && collection.progressDetails.ownedProducts,
    ];

    var ids = new Set();
    sources.forEach(function (source) {
      if (!Array.isArray(source)) return;
      source.forEach(function (entry) {
        var value =
          sanitizeString(entry && (entry.gid || entry.productGid || entry.id)) ||
          sanitizeString(entry);
        if (value) ids.add(value);
      });
    });
    return ids;
  }

  function normalizeCollectionItems(items, ownedIds) {
    return (Array.isArray(items) ? items : [])
      .filter(Boolean)
      .map(function (item) {
        var id =
          sanitizeString(item && (item.gid || item.productGid || item.id || item)) ||
          sanitizeString(item && item.productId);
        var priceValue = resolveItemPriceValue(item);
        var compareAtValue = resolveItemCompareAtPriceValue(item);
        var currency =
          sanitizeString(item && (item.currencyCode || item.currency)) || "USD";
        var owned =
          item && item.owned === true
            ? true
            : id && ownedIds instanceof Set
              ? ownedIds.has(id)
              : false;

        return {
          id: id,
          title:
            cleanProductTitle(item && (item.name || item.title)) ||
            (id ? id.split("/").pop() : "") ||
            "Untitled",
          image:
            sanitizeString(item && item.image) ||
            sanitizeString(item && item.imageUrl) ||
            sanitizeString(item && item.featuredImage && item.featuredImage.url),
          productUrl: buildProductUrl(item),
          handle: sanitizeString(item && (item.handle || item.productHandle)),
          scientificName: sanitizeString(item && (item.species || item.latinName)),
          note: sanitizeString(item && (item.note || item.notes || item.description)),
          notes: sanitizeString(item && (item.note || item.notes || item.description)),
          vendor: sanitizeString(item && item.vendor),
          variants: sanitizeString(item && (item.variantTitle || item.variantName)),
          available:
            typeof (item && item.available) === "boolean" ? !!item.available : null,
          price: priceValue,
          priceAmount: priceValue,
          compareAtPrice: compareAtValue,
          compareAtPriceAmount: compareAtValue,
          priceLabel:
            sanitizeString(item && item.priceLabel) ||
            formatPriceLabel(priceValue, currency),
          compareAtPriceLabel:
            sanitizeString(item && item.compareAtPriceLabel) ||
            formatPriceLabel(compareAtValue, currency),
          currency: currency,
          currencyCode: currency,
          variantId: sanitizeString(item && item.variantId) || null,
          owned: owned,
        };
      });
  }

  function normalizeGardenItems(items) {
    return (Array.isArray(items) ? items : [])
      .filter(Boolean)
      .map(function (item) {
        var priceValue = resolveItemPriceValue(item);
        var compareAtValue = resolveItemCompareAtPriceValue(item);
        var currency =
          sanitizeString(item && (item.currencyCode || item.currency)) || "USD";

        return {
          id:
            sanitizeString(item && (item.id || item.itemId || item.plantId)) ||
            sanitizeString(item && item.productId) ||
            sanitizeString(item && item.productGid) ||
            sanitizeString(item && (item.handle || item.productHandle)),
          title:
            cleanProductTitle(
              item &&
                (item.title ||
                  item.commonName ||
                  item.nickname ||
                  item.name ||
                  item.species),
            ) || "Untitled",
          image:
            sanitizeString(item && item.image) ||
            sanitizeString(item && item.bestImage) ||
            sanitizeString(item && item.imageUrl) ||
            sanitizeString(item && (Array.isArray(item.images) ? item.images[0] : "")) ||
            sanitizeString(item && item.featuredImage && item.featuredImage.url),
          productUrl: buildProductUrl(item),
          handle: sanitizeString(item && (item.handle || item.productHandle)),
          scientificName: sanitizeString(item && (item.species || item.latinName)),
          location: sanitizeString(item && (item.location || item.locationInGarden)),
          note: sanitizeString(item && (item.note || item.notes || item.description)),
          notes: sanitizeString(item && (item.note || item.notes || item.description)),
          vendor: sanitizeString(item && item.vendor),
          variants: sanitizeString(item && (item.variantTitle || item.variantName)),
          available:
            typeof (item && item.available) === "boolean" ? !!item.available : null,
          dateLabel:
            sanitizeString(item && (item.dateLabel || item.addedLabel || item.ownedSinceLabel)),
          price: priceValue,
          priceAmount: priceValue,
          compareAtPrice: compareAtValue,
          compareAtPriceAmount: compareAtValue,
          priceLabel:
            sanitizeString(item && item.priceLabel) ||
            formatPriceLabel(priceValue, currency),
          compareAtPriceLabel:
            sanitizeString(item && item.compareAtPriceLabel) ||
            formatPriceLabel(compareAtValue, currency),
          currency: currency,
          currencyCode: currency,
          variantId: sanitizeString(item && item.variantId) || null,
          productId: sanitizeString(item && item.productId) || null,
          type: sanitizeString(item && item.type) || (buildProductUrl(item) ? "purchased" : "custom"),
          visible: item && item.visible !== false && item && item.isVisible !== false,
        };
      });
  }

  function mapSharedListItems(items) {
    return normalizeListItems(items);
  }

  async function fetchJson(url, fallbackMessage) {
    var response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      var message = fallbackMessage || "Request failed.";
      try {
        var errorData = await response.json();
        if (errorData && typeof errorData.error === "string" && errorData.error.trim()) {
          message = errorData.error.trim();
        }
      } catch (_) {
        // Ignore response parsing failures.
      }
      throw new Error(message);
    }

    return response.json();
  }

  async function postSharedListResponse(body) {
    var payload = {};
    Object.keys(body || {}).forEach(function (key) {
      var value = body[key];
      if (value === undefined || value === null || value === "") return;
      payload[key] = value;
    });

    var response = await fetch(APP_PROXY_BASE + "/v1/shared-lists/respond", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      var message = "Unable to update this shared list right now.";
      try {
        var errorData = await response.json();
        if (errorData?.error) {
          message = String(errorData.error);
        }
      } catch (_) {
        // Ignore JSON parsing errors.
      }
      throw new Error(message);
    }

    return response.json().catch(() => ({}));
  }

  function normalizeSharedInvitation(invitation, sharedQuery) {
    if (!invitation || typeof invitation !== "object") return null;

    var id = sanitizeString(
      invitation.id ||
      invitation.invitationId ||
      invitation.invitation_id ||
      invitation.pendingInvitationId ||
      invitation.pending_invitation_id ||
      invitation.sharedInvitationId ||
      invitation.shared_invitation_id ||
      invitation.sharedListInvitationId ||
      invitation.shareInvitationId ||
      invitation.share_invitation_id ||
      (sharedQuery && sharedQuery.invitationId),
    );
    var listId =
      sanitizeString(invitation.listId || invitation.list_id) ||
      sanitizeString(sharedQuery && sharedQuery.listId);
    var ownerUid =
      sanitizeString(invitation.ownerUid || invitation.owner_uid) ||
      sanitizeString(sharedQuery && sharedQuery.ownerUid);
    var token =
      sanitizeString(invitation.token) ||
      sanitizeString(sharedQuery && (sharedQuery.token || sharedQuery.legacyInviteToken));

    if (!id && (!listId || !token)) return null;

    return {
      id: id,
      listId: listId,
      role:
        sanitizeString(invitation.role || (sharedQuery && sharedQuery.role)) === "editor"
          ? "editor"
          : "viewer",
      status: sanitizeString(invitation.status) || "pending",
      ownerUid: ownerUid,
      token: token,
    };
  }

  function resolveSharedInvitation(payload, sharedQuery, explicitInvitation) {
    var normalized = normalizeSharedInvitation(explicitInvitation, sharedQuery);
    if (normalized) return normalized;

    var viewer = payload && payload.viewer && typeof payload.viewer === "object"
      ? payload.viewer
      : {};
    var list = payload && payload.list && typeof payload.list === "object"
      ? payload.list
      : {};
    var candidates = [
      payload && payload.invitation,
      payload && payload.pendingInvitation,
      payload && payload.sharedInvitation,
      payload && payload.list && payload.list.invitation,
      payload && payload.list && payload.list.pendingInvitation,
      payload && payload.list && payload.list.sharedInvitation,
      payload && payload.invitationId
        ? { id: payload.invitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role }
        : null,
      payload && payload.pendingInvitationId
        ? { id: payload.pendingInvitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role }
        : null,
      payload && payload.sharedInvitationId
        ? { id: payload.sharedInvitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role }
        : null,
      payload && payload.list && payload.list.invitationId
        ? { id: payload.list.invitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role, ownerUid: payload.list.ownerUid }
        : null,
      payload && payload.list && payload.list.pendingInvitationId
        ? { id: payload.list.pendingInvitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role, ownerUid: payload.list.ownerUid }
        : null,
      payload && payload.list && payload.list.sharedInvitationId
        ? { id: payload.list.sharedInvitationId, listId: sharedQuery && sharedQuery.listId, role: sharedQuery && sharedQuery.role, ownerUid: payload.list.ownerUid }
        : null,
      viewer.invitation,
      viewer.pendingInvitation,
      viewer.sharedInvitation,
      viewer.invitationId
        ? {
            id: viewer.invitationId,
            listId: sharedQuery && sharedQuery.listId,
            role: sharedQuery && sharedQuery.role,
          }
        : null,
      viewer.pendingInvitationId
        ? {
            id: viewer.pendingInvitationId,
            listId: sharedQuery && sharedQuery.listId,
            role: sharedQuery && sharedQuery.role,
            ownerUid: viewer.ownerUid,
          }
        : null,
      viewer.sharedInvitationId
        ? {
            id: viewer.sharedInvitationId,
            listId: sharedQuery && sharedQuery.listId,
            role: sharedQuery && sharedQuery.role,
            ownerUid: viewer.ownerUid,
          }
        : null,
    ];

    for (var i = 0; i < candidates.length; i += 1) {
      normalized = normalizeSharedInvitation(candidates[i], sharedQuery);
      if (normalized) return normalized;
    }

    return normalizeSharedInvitation(
      {
        listId: sharedQuery && sharedQuery.listId,
        role: sharedQuery && sharedQuery.role,
        invitationId: sharedQuery && sharedQuery.invitationId,
        ownerUid: sanitizeString(viewer.ownerUid) || sanitizeString(list.ownerUid),
        token: sharedQuery && (sharedQuery.token || sharedQuery.legacyInviteToken),
      },
      sharedQuery,
    );
  }

  function buildInvitationResponseBody(invitation, action) {
    var normalizedAction = action === "deny" ? "deny" : "accept";
    var body = { action: normalizedAction };

    if (!invitation || !invitation.id) {
      throw new Error("This shared list invitation is missing its response token.");
    }

    body.invitationId = invitation.id;
    return body;
  }

  function buildSharedListPrepareBody(sharedQuery, payload, invitation) {
    var viewer = payload && payload.viewer && typeof payload.viewer === "object"
      ? payload.viewer
      : {};
    var list = payload && payload.list && typeof payload.list === "object"
      ? payload.list
      : {};
    var resolvedInvitation =
      invitation && typeof invitation === "object" ? invitation : null;
    var role = sharedQuery && sharedQuery.role === "editor" ? "editor" : "viewer";
    var hasLegacyInviteToken = Boolean(
      sanitizeString(sharedQuery && sharedQuery.legacyInviteToken),
    );
    var tokenSource = sharedQuery && sharedQuery.token
      ? sharedQuery.token
      : !hasLegacyInviteToken && resolvedInvitation && resolvedInvitation.token
        ? resolvedInvitation.token
        : null;
    var token = sanitizeString(tokenSource) || null;
    var legacyInviteToken =
      sanitizeString(
        sharedQuery && sharedQuery.legacyInviteToken
          ? sharedQuery.legacyInviteToken
          : null,
      ) || null;
    var ownerUid =
      sanitizeString(sharedQuery && sharedQuery.ownerUid) ||
      sanitizeString(viewer.ownerUid) ||
      sanitizeString(list.ownerUid) ||
      null;

    return {
      action: "prepare",
      handle: sanitizeString(sharedQuery && sharedQuery.handle) || null,
      ownerUid: ownerUid,
      listId: sanitizeString(sharedQuery && sharedQuery.listId) || null,
      role: role,
      token: token,
      legacyInviteToken: legacyInviteToken,
    };
  }

  function canRespondToStoredInvitation(invitation) {
    return Boolean(sanitizeString(invitation && invitation.id));
  }

  function hasJoinedSharedInvitation(payload, invitation) {
    var viewer = payload && payload.viewer && typeof payload.viewer === "object"
      ? payload.viewer
      : {};
    var status = sanitizeString(invitation && invitation.status).toLowerCase();
    return (
      viewer.isAdded === true ||
      status === "accepted" ||
      status === "active" ||
      status === "joined"
    );
  }

  function showToast(message) {
    if (window.GardenToast?.show) {
      window.GardenToast.show(message);
      return;
    }
    if (window.GardenShareMenu?.showToast) {
      window.GardenShareMenu.showToast(message);
      return;
    }
    console.info("[Shared List]", message);
  }

  function resolvePublicItems(route, payload) {
    if (!payload || typeof payload !== "object") {
      return [];
    }

    if (route.type === "list") {
      var list = payload.list && typeof payload.list === "object" ? payload.list : null;
      return normalizeListItems(
        (list && list.items) || payload.items || payload.products || [],
      );
    }

    if (route.type === "collection") {
      var collection =
        payload.collection && typeof payload.collection === "object"
          ? payload.collection
          : payload;
      var ownedIds = extractCollectionOwnedIds(payload, collection);
      return normalizeCollectionItems(
        payload.products ||
          (collection && collection.items) ||
          (collection && collection.products) ||
          [],
        ownedIds,
      );
    }

    return normalizeGardenItems(
      payload.items ||
        payload.plants ||
        payload.products ||
        (payload.garden && payload.garden.items) ||
        [],
    );
  }

  function resolveCount(value, fallback) {
    var count = Number(value);
    return Number.isFinite(count) && count >= 0 ? count : fallback;
  }

  function buildPublicDocumentStats(route, items) {
    if (route.type === "collection") {
      var ownedCount = items.filter(function (item) {
        return item && item.owned === true;
      }).length;
      return {
        label: "Owned",
        value: ownedCount + " / " + items.length,
      };
    }

    return {
      label: route.type === "plants" ? "Total Plants" : "Total Items",
      value: String(items.length),
    };
  }

  function formatPublicGardenZoneLabel(value) {
    var zone = sanitizeString(value);
    if (!zone) return "";
    return /\bzone\b/i.test(zone)
      ? zone.replace(/\bzone\b/i, "Zone")
      : "Zone " + zone;
  }

  function resolvePublicGardenZoneLabel(payload, profile) {
    var source = profile && typeof profile === "object" ? profile : {};
    var candidates = [
      source.zone,
      source.hardinessZone,
      source.usdaZone,
      source.gardenZone,
      payload && payload.zone,
      payload && payload.hardinessZone,
      payload && payload.usdaZone,
      payload && payload.gardenZone,
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      var label = formatPublicGardenZoneLabel(candidates[i]);
      if (label) return label;
    }
    return "Zone not set";
  }

  function formatPublicGardenSinceLabel(value) {
    var raw = sanitizeString(value);
    if (!raw) return "";
    var parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return String(parsed.getFullYear());
    }
    var yearMatch = raw.match(/\b(19|20)\d{2}\b/);
    return yearMatch ? yearMatch[0] : raw;
  }

  function resolvePublicGardenSinceLabel(payload, profile) {
    var source = profile && typeof profile === "object" ? profile : {};
    var candidates = [
      source.since,
      source.memberSince,
      source.gardenSince,
      source.gardenCreatedAt,
      source.createdAt,
      source.created_at,
      source.createdOn,
      payload && payload.since,
      payload && payload.memberSince,
      payload && payload.gardenSince,
      payload && payload.gardenCreatedAt,
      payload && payload.createdAt,
      payload && payload.created_at,
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      var label = formatPublicGardenSinceLabel(candidates[i]);
      if (label) return label;
    }
    return "Not set";
  }

  function buildPublicDocumentConfig(route) {
    return {
      images: true,
      prices: true,
      notes: route.type !== "collection",
      scientificName: route.type === "plants",
      location: false,
      age: false,
      status: route.type === "collection",
      availability: false,
      vendor: true,
      variants: true,
      tags: false,
      stats: true,
      qr: false,
      itemQr: false,
      lines: false,
      listStyle: "numbered",
      theme: "mono",
      orientation: "portrait",
      pageSize: "A4",
    };
  }

  function normalizePublicBadgeEntries(entries) {
    return (Array.isArray(entries) ? entries : [])
      .filter(Boolean)
      .map(function (entry, index) {
        var id =
          sanitizeString(entry && (entry.id || entry.sourceId)) ||
          "badge-" + String(index + 1);
        var label =
          sanitizeString(entry && (entry.name || entry.label || entry.title)) ||
          "Badge";
        var iconUrl = sanitizeString(entry && (entry.iconUrl || entry.image));
        var iconText =
          sanitizeString(entry && (entry.icon || entry.iconEmoji || entry.emoji)) ||
          "🏆";
        var kind =
          sanitizeString(entry && entry.kind) === "collection"
            ? "collection"
            : sanitizeString(entry && entry.kind) === "achievement"
              ? "achievement"
              : "";

        return {
          id: id,
          label: label,
          iconUrl: iconUrl || "",
          iconText: iconText,
          kind: kind,
        };
      })
      .slice(0, 40);
  }

  function normalizeOtherPublicLists(entries, excludeId) {
    var normalizedExclude = sanitizeString(excludeId);
    return (Array.isArray(entries) ? entries : [])
      .filter(Boolean)
      .map(function (entry) {
        var id = sanitizeString(entry && entry.id);
        if (!id) return null;
        var href = sanitizeString(entry && entry.href);
        return {
          id: id,
          name: sanitizeString(entry && entry.name) || "Untitled List",
          itemsCount:
            typeof (entry && entry.itemsCount) === "number" && entry.itemsCount >= 0
              ? entry.itemsCount
              : 0,
          href: href || "",
        };
      })
      .filter(function (entry) {
        return entry && entry.id !== normalizedExclude;
      })
      .slice(0, 8);
  }

  function buildPublicCollectionNotice(viewer) {
    if (viewer && viewer.isOwner) {
      return "This is your collection log. Open it in My Garden to manage your own progress.";
    }
    if (viewer && viewer.isSignedIn) {
      return "See your own progress on this collection by opening it in My Garden.";
    }
    return "Sign in or create an account to compare your own progress against this collection.";
  }

  function buildPublicCollectionActions(payload) {
    var actions = [];
    var viewer =
      payload && payload.viewer && typeof payload.viewer === "object"
        ? payload.viewer
        : {};
    var profilePath =
      sanitizeString(payload && payload.profilePath) ||
      sanitizeString(payload && payload.profile && payload.profile.profilePath);
    var loginUrl = sanitizeString(viewer.loginUrl);
    var accountUrl = sanitizeString(viewer.accountUrl);

    if (!viewer.isSignedIn && loginUrl) {
      actions.push({
        label: "Create account / Sign in",
        href: loginUrl,
      });
    } else if (accountUrl) {
      actions.push({
        label: viewer.isOwner ? "Open in My Garden" : "See My Progress",
        href: accountUrl,
      });
    }

    if (profilePath) {
      actions.push({
        label: "View Garden",
        href: profilePath,
        tone: "secondary",
      });
    }

    return actions;
  }

  function buildPublicCollectionProfileCard(payload, handle) {
    var profile =
      payload && payload.profile && typeof payload.profile === "object"
        ? payload.profile
        : {};
    var displayName =
      sanitizeString(profile.displayName) ||
      sanitizeString(profile.gardenName) ||
      sanitizeString(profile.firstName) ||
      sanitizeString(handle) ||
      "Collector";
    var badgeEntries = normalizePublicBadgeEntries(payload && payload.badges);

    return {
      title: displayName,
      gardenName: sanitizeString(profile.gardenName) || "",
      handle: handle ? "@" + handle : "",
      imageUrl: sanitizeString(profile.profileImageUrl) || "",
      roleLabel: "Owner",
      profilePath: sanitizeString(payload && payload.profilePath) || "",
      subtitle:
        sanitizeString(profile.gardenName) &&
        sanitizeString(profile.gardenName) !== displayName
          ? sanitizeString(profile.gardenName)
          : "",
      badges: badgeEntries,
      otherLists: normalizeOtherPublicLists(payload && payload.publicLists, null),
      tier:
        payload && payload.tier && typeof payload.tier === "object"
          ? {
              name: sanitizeString(payload.tier.name) || "",
              badgeUrl: sanitizeString(payload.tier.badgeUrl) || "",
            }
          : null,
    };
  }

  function normalizeSharedPerson(entry, fallbackRole) {
    if (!entry || typeof entry !== "object") return null;
    var name =
      sanitizeString(entry.displayName) ||
      sanitizeString(entry.name) ||
      sanitizeString(entry.editorName) ||
      sanitizeString(entry.ownerName) ||
      sanitizeString(entry.gardenName) ||
      sanitizeString(entry.firstName) ||
      sanitizeString(entry.handle) ||
      sanitizeString(entry.editorHandle);
    var handle =
      sanitizeString(entry.handle) ||
      sanitizeString(entry.editorHandle) ||
      sanitizeString(entry.ownerHandle);
    var role = sanitizeString(entry.role || fallbackRole).toLowerCase();
    if (!name && !handle) return null;

    return {
      name: name || handle,
      handle: handle ? "@" + handle.replace(/^@+/, "") : "",
      role: role === "owner" ? "Owner" : role === "viewer" ? "Viewer" : "Editor",
      imageUrl:
        sanitizeString(entry.profileImageUrl) ||
        sanitizeString(entry.imageUrl) ||
        sanitizeString(entry.editorProfileImage) ||
        "",
    };
  }

  function collectSharedPeople(payload, sharedQuery) {
    var source = payload && typeof payload === "object" ? payload : {};
    var profile =
      source.profile && typeof source.profile === "object"
        ? source.profile
        : {};
    var list =
      source.list && typeof source.list === "object"
        ? source.list
        : {};
    var people = [];
    var seen = {};

    function addPerson(person) {
      if (!person) return;
      var key = (person.handle || person.name || "").toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      people.push(person);
    }

    addPerson(
      normalizeSharedPerson(
        {
          displayName:
            profile.displayName ||
            profile.gardenName ||
            profile.firstName ||
            source.ownerName ||
            list.ownerName ||
            (sharedQuery && sharedQuery.handle),
          handle:
            profile.handle ||
            source.ownerHandle ||
            list.ownerHandle ||
            (sharedQuery && sharedQuery.handle),
          profileImageUrl:
            profile.profileImageUrl ||
            profile.imageUrl ||
            source.ownerProfileImage ||
            list.ownerProfileImage,
        },
        "owner",
      ),
    );

    [
      source.collaborators,
      source.editors,
      source.members,
      list && list.collaborators,
      list && list.editors,
      list && list.members,
    ].forEach(function (entries) {
      (Array.isArray(entries) ? entries : []).forEach(function (entry) {
        addPerson(normalizeSharedPerson(entry, entry && entry.role));
      });
    });

    return people.slice(0, 8);
  }

  function buildSharedProfileCard(payload, sharedQuery) {
    var people = collectSharedPeople(payload, sharedQuery);
    if (!people.length) return null;
    var owner = people.find(function (person) {
      return person.role === "Owner";
    }) || people[0];
    var editorCount = people.filter(function (person) {
      return person.role === "Editor";
    }).length;
    var viewerCount = people.filter(function (person) {
      return person.role === "Viewer";
    }).length;
    var summaryParts = ["Owner"];
    if (editorCount) summaryParts.push(editorCount + " editor" + (editorCount === 1 ? "" : "s"));
    if (viewerCount) summaryParts.push(viewerCount + " viewer" + (viewerCount === 1 ? "" : "s"));

    var badgeEntries = normalizePublicBadgeEntries(payload && payload.badges);
    var tier = payload && payload.tier && typeof payload.tier === "object" ? payload.tier : null;
    var profile =
      payload && payload.profile && typeof payload.profile === "object"
        ? payload.profile
        : {};

    return {
      title: owner.name || "Shared garden",
      gardenName: sanitizeString(profile.gardenName) || "",
      handle: owner.handle || "",
      imageUrl: owner.imageUrl || "",
      roleLabel: owner.role || "Owner",
      subtitle: summaryParts.join(" • "),
      profilePath: sanitizeString(payload && payload.profilePath) || "",
      badges: badgeEntries,
      otherLists: normalizeOtherPublicLists(payload && payload.publicLists, null),
      tier: tier ? { name: sanitizeString(tier.name) || "", badgeUrl: sanitizeString(tier.badgeUrl) || "" } : null,
      people: people.filter(function (p) { return p.role !== "Owner"; }),
    };
  }

  function buildPublicListProfileCard(payload, handle, currentListId) {
    var profile =
      payload && payload.profile && typeof payload.profile === "object"
        ? payload.profile
        : {};
    var normalizedHandle = sanitizeString(handle);
    var gardenName = sanitizeString(profile.gardenName);
    var displayName =
      sanitizeString(profile.displayName) ||
      gardenName ||
      sanitizeString(profile.firstName) ||
      normalizedHandle ||
      "Collector";
    if (!normalizedHandle && !displayName) return null;
    var tier =
      payload && payload.tier && typeof payload.tier === "object"
        ? payload.tier
        : null;

    return {
      title: displayName,
      gardenName: gardenName || "",
      handle: normalizedHandle ? "@" + normalizedHandle : "",
      imageUrl: sanitizeString(profile.profileImageUrl) || "",
      roleLabel: "Owner",
      profilePath: sanitizeString(payload && payload.profilePath) || "",
      badges: normalizePublicBadgeEntries(payload && payload.badges),
      otherLists: normalizeOtherPublicLists(payload && payload.publicLists, currentListId),
      tier: tier
        ? {
            name: sanitizeString(tier.name) || "",
            badgeUrl: sanitizeString(tier.badgeUrl) || "",
          }
        : null,
    };
  }

  function buildPublicCollectionStats(payload, collection, items) {
    var ownedCount = resolveCount(
      payload && payload.progress && payload.progress.owned,
      items.filter(function (item) {
        return item && item.owned === true;
      }).length,
    );
    var totalCount = resolveCount(
      payload && payload.progress && payload.progress.total,
      resolveCount(
        collection && (collection.productsCount || collection.itemsCount),
        items.length,
      ),
    );
    var percentage = resolveCount(
      payload && payload.progress && payload.progress.percentage,
      totalCount > 0 ? Math.round((ownedCount / totalCount) * 100) : 0,
    );
    var missingCount = Math.max(totalCount - ownedCount, 0);

    return [
      {
        label: "Complete",
        value: String(percentage) + "%",
        tone: "accent",
      },
      {
        label: "Owned",
        value: String(ownedCount),
      },
      {
        label: "Missing",
        value: String(missingCount),
      },
      {
        label: "Total",
        value: String(totalCount),
      },
    ];
  }

  function buildPublicPreviewConfig(route, payload, options) {
    var profile =
      payload && payload.profile && typeof payload.profile === "object"
        ? payload.profile
        : {};
    var list =
      payload && payload.list && typeof payload.list === "object"
        ? payload.list
        : {};
    var collection =
      payload && payload.collection && typeof payload.collection === "object"
        ? payload.collection
        : {};
    var items = resolvePublicItems(route, payload);
    var handle = sanitizeString(profile.handle) || sanitizeString(route.handle);
    var title =
      route.type === "list"
        ? sanitizeString(list.name) || "Shared List"
        : route.type === "collection"
          ? sanitizeString(collection.name) || "Collection Progress"
          : sanitizeString(profile.gardenName) || "Public Garden";
    var gardenName = sanitizeString(profile.gardenName);
    var subtitle =
      route.type === "list" || route.type === "collection"
        ? gardenName || (handle ? "@" + handle : "")
        : handle
          ? "@" + handle
          : "";
    var notice =
      route.type === "list"
        ? sanitizeString(list.description) || sanitizeString(payload && payload.description)
        : route.type === "collection"
          ? sanitizeString(collection.description) ||
            sanitizeString(payload && payload.description)
          : sanitizeString(profile.bio) || sanitizeString(payload && payload.description);
    var itemsCount =
      route.type === "list"
        ? resolveCount(list.itemsCount, items.length)
        : route.type === "collection"
          ? resolveCount(
              collection.productsCount || collection.itemsCount,
              items.length,
            )
          : resolveCount(
              payload && (payload.itemsCount || payload.publicItemsCount),
              items.length,
            );
    var gardenZoneLabel =
      route.type === "plants"
        ? resolvePublicGardenZoneLabel(payload, profile)
        : "";
    var gardenSinceLabel =
      route.type === "plants"
        ? resolvePublicGardenSinceLabel(payload, profile)
        : "";
    var publicViewer =
      payload && payload.viewer && typeof payload.viewer === "object"
        ? payload.viewer
        : {};
    var useCopyLayout = route.type === "list" && !publicViewer.isOwner;

    return {
      type: route.type === "plants" ? "plants" : route.type,
      appProxyBase: APP_PROXY_BASE,
      listId: route.type === "plants" ? "garden" : route.type === "list" ? route.resourceId : null,
      collectionId: route.type === "collection" ? route.resourceId : null,
      title: title,
      subtitle: subtitle,
      hideOwnerBanner: useCopyLayout,
      copyAction: useCopyLayout
        ? buildSharedCopyAction({
            viewer: publicViewer,
            items: items,
            defaultName: title,
          })
        : null,
      ownerLabel: route.type === "plants" ? "" : subtitle,
      roleLabel:
        route.type === "collection"
          ? "Collection Progress"
          : route.type === "list"
            ? "Public List"
            : "",
      itemsCount: itemsCount,
      notice:
        route.type === "collection"
          ? buildPublicCollectionNotice(payload && payload.viewer)
          : notice,
      items: items,
      profileCard:
        route.type === "collection"
          ? buildPublicCollectionProfileCard(payload, handle)
          : route.type === "list"
            ? buildPublicListProfileCard(payload, handle, route.resourceId)
            : null,
      summaryStats:
        route.type === "plants"
          ? [
              {
                label: "Zone",
                value: gardenZoneLabel,
              },
              {
                label: "Since",
                value: gardenSinceLabel,
              },
            ]
          : route.type === "collection"
          ? buildPublicCollectionStats(payload, collection, items)
          : null,
      actions:
        route.type === "collection"
          ? buildPublicCollectionActions(payload)
          : null,
      documentStats:
        route.type === "plants"
          ? {
              label: "Zone",
              value: gardenZoneLabel,
            }
          : buildPublicDocumentStats(route, items),
      documentConfig: buildPublicDocumentConfig(route),
      onClose: options && typeof options.onClose === "function" ? options.onClose : null,
    };
  }

  function buildPublicErrorMessage(route) {
    if (!route) return "This public preview is unavailable.";
    if (route.type === "collection") return "This collection could not be loaded.";
    if (route.type === "list") return "This list could not be loaded.";
    return "This public garden could not be loaded.";
  }

  async function loadPublicPreviewPayload(route) {
    return fetchJson(buildPublicPreviewUrl(route), buildPublicErrorMessage(route));
  }

  function navigateAfterPublicRouteClose(routeState) {
    if (!routeState) return;

    if (routeState.closeMode === "history" && window.history.length > 1) {
      window.history.back();
      return;
    }

    if (
      routeState.closeMode === "initial" &&
      window.history.length > 1 &&
      isSameOriginReferrer(document.referrer)
    ) {
      window.history.back();
      return;
    }

    window.location.href = "/";
  }

  function handlePublicRouteClose(routeState) {
    if (!routeState) return;
    if (activePublicRoute && activePublicRoute.key === routeState.key) {
      activePublicRoute = null;
    }
    if (suppressPublicRouteCloseNavigation) {
      return;
    }
    navigateAfterPublicRouteClose(routeState);
  }

  function closePublicRouteModal(skipNavigation) {
    if (!activePublicRoute) return;

    publicRouteRequestId += 1;
    activePublicRoute = null;
    suppressPublicRouteCloseNavigation = !!skipNavigation;
    try {
      if (window.GardenDocumentViewer?.closeSharedList) {
        window.GardenDocumentViewer.closeSharedList();
      }
    } finally {
      suppressPublicRouteCloseNavigation = false;
    }
  }

  async function openPublicPreview(route, options) {
    await ensureDocumentViewer();

    var requestId = ++publicRouteRequestId;
    var payload = await loadPublicPreviewPayload(route);
    if (requestId !== publicRouteRequestId) {
      return;
    }

    var routeState = {
      key: route.key,
      closeMode:
        options && options.closeMode === "history" ? "history" : "initial",
    };

    activePublicRoute = routeState;

    var externalOnClose = options && typeof options.onClose === "function" ? options.onClose : null;

    try {
      window.GardenDocumentViewer.openSharedList(
        buildPublicPreviewConfig(route, payload, {
          onClose: function () {
            if (externalOnClose) {
              if (activePublicRoute && activePublicRoute.key === routeState.key) {
                activePublicRoute = null;
              }
              externalOnClose();
            } else {
              handlePublicRouteClose(routeState);
            }
          },
        }),
      );
    } catch (error) {
      if (activePublicRoute && activePublicRoute.key === routeState.key) {
        activePublicRoute = null;
      }
      throw error;
    }
  }

  async function syncPublicRouteFromLocation(options) {
    var route = parsePublicGardenRoute(window.location.href);
    if (!route) {
      if (activePublicRoute) {
        closePublicRouteModal(true);
      }
      return;
    }

    if (activePublicRoute && activePublicRoute.key === route.key) {
      return;
    }

    try {
      await openPublicPreview(route, {
        closeMode:
          options && options.closeMode === "history" ? "history" : "initial",
      });
    } catch (error) {
      console.error("[Shared List Host] Failed to load public preview:", error);
      showToast(
        error instanceof Error
          ? error.message
          : buildPublicErrorMessage(route),
      );
    }
  }

  function interceptPublicRouteClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    var target = event.target;
    if (!target) return;

    var originElement =
      typeof target.closest === "function"
        ? target
        : target.parentElement || null;
    if (!originElement || typeof originElement.closest !== "function") {
      return;
    }

    var anchor = originElement.closest("a[href]");
    if (!anchor) return;
    if (anchor.hasAttribute("download")) return;
    if (anchor.target && anchor.target !== "_self") return;

    var route = parsePublicGardenRoute(anchor.href);
    if (!route) return;

    event.preventDefault();

    if (window.location.pathname + window.location.search + window.location.hash !== route.path) {
      window.history.pushState({ mrMaplePublicGardenModal: true }, "", route.path);
    }

    openPublicPreview(route, { closeMode: "history" }).catch(function (error) {
      console.error("[Shared List Host] Failed to open public preview:", error);
      showToast(
        error instanceof Error
          ? error.message
          : buildPublicErrorMessage(route),
      );
      window.location.href = route.href;
    });
  }

  function buildSharedNotice(payload, invitation, state) {
    var isEditInvite =
      (invitation && normalizeRole(invitation.role) === "editor") ||
      (state && normalizeRole(state.role) === "editor");

    if (invitation && (invitation.id || invitation.token)) {
      if (hasJoinedSharedInvitation(payload, invitation)) {
        return isEditInvite
          ? "Editor invite joined: you can edit items in this shared list from My Garden."
          : "View-only invite joined: this shared list is saved in My Garden.";
      }
      return isEditInvite
        ? "Editor invite: joining lets you add, remove, and manage items in this shared list. The owner still controls sharing and deletion."
        : "View-only invite: you can preview this list and save a copy to edit your own version.";
    }
    if (!payload.viewer?.isSignedIn) {
      return isEditInvite
        ? "Editor invite: sign in or create an account to join and help manage this list."
        : "View-only invite: preview this list now, then sign in to save your own copy.";
    }
    if (payload.viewer?.isOwner) {
      return "";
    }
    if (payload.viewer?.isAdded) {
      return payload.viewer?.canEdit
        ? "Editor access active: this shared list is already in My Garden."
        : "View-only access active: this shared list is already in My Garden.";
    }
    return isEditInvite
      ? "Editor invite: join to edit this shared list in My Garden."
      : "View-only invite: save a copy to keep and edit your own version.";
  }

  function buildSharedActions(payload, sharedQuery, invitation, options) {
    options = options || {};
    var actions = [];
    var viewer = payload.viewer || {};
    var myGardenUrl = viewer.myGardenUrl || "/account?garden-view=lists";
    var resolvedInvitation = resolveSharedInvitation(payload, sharedQuery, invitation);
    var isEditInvite =
      !!resolvedInvitation &&
      ((resolvedInvitation && resolvedInvitation.role) === "editor" ||
        sharedQuery.role === "editor");
    var isViewOnlySharedList = !isEditInvite && sharedQuery.role !== "editor";

    if (!viewer.isSignedIn) {
      if (isViewOnlySharedList) {
        actions.push({
          label: "Save a Copy",
          href: buildAccountAuthUrl(
            window.Theme?.routes?.account_login_url || "/account/login",
          ),
        });
        actions.push({
          label: "Sign in",
          tone: "secondary",
          href: buildAccountAuthUrl(
            window.Theme?.routes?.account_login_url || "/account/login",
          ),
        });
      } else {
        actions.push({
          label: "Sign in",
          href: buildAccountAuthUrl(
            window.Theme?.routes?.account_login_url || "/account/login",
            "prepare",
          ),
        });
        actions.push({
          label: "Create account",
          tone: "secondary",
          href: buildAccountAuthUrl(
            window.Theme?.routes?.account_register_url || "/account/register",
            "prepare",
          ),
        });
      }
      return actions;
    }

    if (viewer.isOwner) {
      actions.push({
        label: "Open in My Garden",
        href: myGardenUrl,
      });
      return actions;
    }

    if (isEditInvite && resolvedInvitation) {
      var joined = hasJoinedSharedInvitation(payload, resolvedInvitation);
      if (joined) {
        actions.push({
          label: "Open in My Garden",
          href: myGardenUrl,
        });
        actions.push({
          label: "Leave",
          tone: "secondary",
          onClick: function () {
            return (options.onLeave || options.onDeny)?.(resolvedInvitation);
          },
        });
        return actions;
      }
      actions.push({
        label: "Join",
        loadingLabel: "Joining...",
        onClick: function () {
          if (!canRespondToStoredInvitation(resolvedInvitation)) {
            return (
              options.onJoinPrepared || options.onPrepare
            )?.(sharedQuery, payload, resolvedInvitation);
          }
          return (options.onJoin || options.onAccept)?.(resolvedInvitation);
        },
      });
      return actions;
    }

    if (viewer.isAdded) {
      actions.push({
        label: "Open in My Garden",
        href: myGardenUrl,
      });
      return actions;
    }

    if (isViewOnlySharedList) {
      actions.push({
        label: "Save a Copy",
        loadingLabel: "Saving copy...",
        onClick: function () {
          return options.onSaveCopy?.(sharedQuery, payload);
        },
      });
      return actions;
    }

    actions.push({
      label: options.prepareLabel || "Add to My Garden",
      loadingLabel: "Adding...",
      onClick: function () {
        return options.onPrepare?.(sharedQuery, payload);
      },
    });
    return actions;
  }

  async function openSharedListModal(payload, sharedQuery, options) {
    options = options || {};
    await ensureDocumentViewer();

    var list = payload.list || {};
    var invitation = resolveSharedInvitation(payload, sharedQuery, options.invitation);
    var isEditorInvite = sharedQuery.role === "editor";
    var role = isEditorInvite ? "Editor invite" : "View-only invite";
    var ownerLabel = payload.profile?.handle
      ? "Shared by @" + payload.profile.handle
      : payload.profile?.firstName || payload.profile?.gardenName || "Shared list";
    var viewer = payload.viewer || {};
    var copyItems = mapSharedListItems(list.items);
    var useCopyLayout = !isEditorInvite && !viewer.isOwner && !viewer.isAdded;

    window.GardenDocumentViewer.openSharedList({
      type: "list",
      appProxyBase: APP_PROXY_BASE,
      listId: list.id || sharedQuery.listId,
      title: list.name || "Shared List",
      subtitle: payload.profile?.gardenName || payload.profile?.bio || "",
      ownerLabel: ownerLabel,
      roleLabel: role,
      accessMode: isEditorInvite ? "editor" : "viewer",
      hideOwnerBanner: useCopyLayout,
      copyAction: useCopyLayout
        ? buildSharedCopyAction({
            viewer: viewer,
            items: copyItems,
            defaultName: list.name || "Shared List",
          })
        : null,
      itemsCount:
        typeof list.itemsCount === "number"
          ? list.itemsCount
          : Array.isArray(list.items)
            ? list.items.length
            : 0,
      headerActions: buildSharedActions(payload, sharedQuery, invitation, options),
      notice: buildSharedNotice(payload, invitation, sharedQuery),
      items: copyItems,
      sharedHeader: buildSharedProfileCard(payload, sharedQuery),
      profileCard: buildSharedProfileCard(payload, sharedQuery),
      onClose: typeof options.onClose === "function" ? options.onClose : null,
      documentConfig: {
        images: true,
        prices: true,
        notes: false,
        scientificName: false,
        location: false,
        age: false,
        status: false,
        availability: false,
        vendor: true,
        variants: true,
        tags: false,
        stats: true,
        qr: false,
        itemQr: false,
        lines: false,
        listStyle: "numbered",
        theme: "mono",
        orientation: "portrait",
        pageSize: "A4",
      },
      pdfUrl:
        payload.viewer?.isOwner === true
          ? APP_PROXY_BASE +
            "/v1/share/list-pdf/" +
            encodeURIComponent(String(list.id || sharedQuery.listId))
          : "",
    });
  }

  async function loadSharedListPayload(sharedQuery) {
    return fetchJson(
      buildSharedPreviewUrl(sharedQuery),
      "This shared list could not be loaded.",
    );
  }

  async function prepareSharedList(sharedQuery, payload, invitation) {
    var response = await postSharedListResponse(
      buildSharedListPrepareBody(sharedQuery, payload, invitation),
    );

    if (response?.redirectUrl) {
      window.location.href = response.redirectUrl;
      return;
    }

    throw new Error("Unable to add this shared list right now.");
  }

  function resolvePreparedResponseInvitation(response, sharedQuery) {
    if (!response || typeof response !== "object") {
      return null;
    }

    var candidates = [
      response.invitation,
      response.pendingInvitation,
      response.sharedInvitation,
      response.list && response.list.invitation,
      response.list && response.list.pendingInvitation,
      response.list && response.list.sharedInvitation,
      response.invitationId
        ? {
            id: response.invitationId,
            listId: sharedQuery && sharedQuery.listId,
            role: sharedQuery && sharedQuery.role,
          }
        : null,
      response.pendingInvitationId
        ? {
            id: response.pendingInvitationId,
            listId: sharedQuery && sharedQuery.listId,
            role: sharedQuery && sharedQuery.role,
          }
        : null,
    ];

    for (var i = 0; i < candidates.length; i += 1) {
      var normalized = normalizeSharedInvitation(candidates[i], sharedQuery);
      if (canRespondToStoredInvitation(normalized)) {
        return normalized;
      }
    }

    return null;
  }

  async function joinPreparedSharedList(sharedQuery, payload, invitation) {
    var response = await postSharedListResponse(
      buildSharedListPrepareBody(sharedQuery, payload, invitation),
    );
    var preparedInvitation = resolvePreparedResponseInvitation(response, sharedQuery);

    if (
      preparedInvitation &&
      sanitizeString(preparedInvitation.status).toLowerCase() !== "accepted"
    ) {
      return acceptInvitation(preparedInvitation);
    }

    if (response?.redirectUrl) {
      showToast("Shared list joined.");
      window.location.href = response.redirectUrl;
      return;
    }

    if (response?.status === "accepted" || response?.status === "owner") {
      showToast("Shared list joined.");
      window.location.href = "/account?garden-view=lists";
      return;
    }

    throw new Error("Unable to add this shared list right now.");
  }

  async function acceptInvitation(invitation) {
    var response = await postSharedListResponse(
      buildInvitationResponseBody(invitation, "accept"),
    );
    showToast("Shared list joined.");
    if (response?.redirectUrl) {
      window.location.href = response.redirectUrl;
      return;
    }
    window.location.href = "/account?garden-view=lists";
  }

  async function denyInvitation(invitation, reloadCallback) {
    await postSharedListResponse(
      buildInvitationResponseBody(invitation, "deny"),
    );
    showToast("Shared list left.");
    if (typeof reloadCallback === "function") {
      reloadCallback();
      return;
    }
    window.location.reload();
  }

  async function leaveSharedList(invitation, reloadCallback) {
    var ownerUid = sanitizeString(invitation && invitation.ownerUid);
    var listId = sanitizeString(invitation && invitation.listId);

    if (ownerUid && listId) {
      var response = await fetch(APP_PROXY_BASE + "/v1/shared-lists", {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ownerUid: ownerUid,
          listId: listId,
        }),
      });

      if (!response.ok) {
        var payload = await response.json().catch(function () {
          return null;
        });
        var message = payload && (payload.error || payload.message)
          ? String(payload.error || payload.message)
          : "Unable to leave this shared list.";
        throw new Error(message);
      }

      showToast("Shared list left.");
      if (typeof reloadCallback === "function") {
        reloadCallback();
        return;
      }
      window.location.reload();
      return;
    }

    await denyInvitation(invitation, reloadCallback);
  }

  async function openInvitationPreview(invitation, options) {
    if (!invitation || !invitation.ownerHandle || !invitation.listId) {
      return;
    }

    var sharedQuery = {
      handle: invitation.ownerHandle,
      listId: invitation.listId,
      role: invitation.role === "editor" ? "editor" : "viewer",
      token:
        invitation.tokenType !== "legacyInvite" &&
        typeof invitation.token === "string" &&
        invitation.token.trim()
          ? invitation.token.trim()
          : null,
      legacyInviteToken:
        invitation.tokenType === "legacyInvite" ? invitation.token || null : null,
      autoAfterAuth: false,
    };

    var payload;
    try {
      payload = await loadSharedListPayload(sharedQuery);
    } catch (_) {
      payload = {
        profile: {
          handle: invitation.ownerHandle,
          gardenName: invitation.ownerName || "Shared list",
        },
        list: {
          id: invitation.listId,
          name: invitation.listName || "Shared list",
          description: invitation.listDescription || "",
          itemsCount: invitation.itemsCount || 0,
          items: [],
        },
        viewer: {
          isSignedIn: true,
          isOwner: false,
          isAdded: false,
          canEdit: false,
          ownerUid: invitation.ownerUid,
          myGardenUrl: "/account?garden-view=lists",
        },
      };
    }

    await openSharedListModal(payload, sharedQuery, {
      invitation: invitation,
      onJoin: function () {
        return acceptInvitation(invitation).then(function () {
          options?.onResolved?.();
        });
      },
      onLeave: function () {
        return leaveSharedList(invitation, options?.onResolved);
      },
      onSaveCopy: function (query, nextPayload) {
        return saveSharedListCopy(query, nextPayload);
      },
      onJoinPrepared: function (query, nextPayload, nextInvitation) {
        return joinPreparedSharedList(query, nextPayload, nextInvitation).then(function () {
          options?.onResolved?.();
        });
      },
      onPrepare: function (query, nextPayload, nextInvitation) {
        return prepareSharedList(query, nextPayload, nextInvitation).then(function () {
          options?.onResolved?.();
        });
      },
    });
  }

  async function bootstrapPublicCollection(collectionQuery) {
    var route = {
      url: new URL(window.location.href),
      href: window.location.href,
      key: "collection-" + collectionQuery.collectionId,
      path: window.location.pathname + window.location.search,
      handle: collectionQuery.handle,
      type: "collection",
      resourceId: collectionQuery.collectionId,
      token: null,
      role: null,
    };

    try {
      await openPublicPreview(route, {
        closeMode: "initial",
        onClose: clearPublicCollectionQueryParams,
      });
    } catch (error) {
      console.error("[Shared List Host] Failed to load public collection:", error);
      showToast(
        error instanceof Error
          ? error.message
          : "This collection could not be loaded.",
      );
    }
  }

  async function bootstrapFromUrl() {
    syncAccountAuthReturnUrl();

    var sharedQuery = getSharedQueryParams();
    if (!sharedQuery) {
      var collectionQuery = getPublicCollectionQueryParams();
      if (collectionQuery) {
        await bootstrapPublicCollection(collectionQuery);
        return;
      }

      await syncPublicRouteFromLocation({ closeMode: "initial" });
      return;
    }

    try {
      var payload = await loadSharedListPayload(sharedQuery);
      await openSharedListModal(payload, sharedQuery, {
        prepareLabel: sharedQuery.autoAfterAuth
          ? "Adding to My Garden…"
          : "Add to My Garden",
        onClose: clearSharedHostQueryParams,
        onJoin: function (invitation) {
          return acceptInvitation(invitation).then(clearSharedHostQueryParams);
        },
        onLeave: function (invitation) {
          return leaveSharedList(invitation, clearSharedHostQueryParams);
        },
        onSaveCopy: function (query, nextPayload) {
          return saveSharedListCopy(query, nextPayload).catch(function (error) {
            showToast(
              error instanceof Error
                ? error.message
                : "Unable to save a copy right now.",
            );
          });
        },
        onJoinPrepared: function (query, nextPayload, nextInvitation) {
          return joinPreparedSharedList(query, nextPayload, nextInvitation).catch(function (error) {
            showToast(
              error instanceof Error
                ? error.message
                : "Unable to add this shared list right now.",
            );
          });
        },
        onPrepare: function (query, nextPayload) {
          return prepareSharedList(query, nextPayload).catch(function (error) {
            showToast(
              error instanceof Error
                ? error.message
                : "Unable to add this shared list right now.",
            );
          });
        },
      });

      var autoInvitation = resolveSharedInvitation(payload, sharedQuery);
      var shouldAutoPrepareAfterAuth =
        sharedQuery.autoAfterAuth &&
        (sharedQuery.role === "editor" || autoInvitation?.role === "editor");
      if (
        shouldAutoPrepareAfterAuth &&
        payload.viewer?.isSignedIn &&
        !payload.viewer?.isOwner &&
        !payload.viewer?.isAdded
      ) {
        window.setTimeout(function () {
          if (!getSharedQueryParams()) {
            return;
          }
          joinPreparedSharedList(sharedQuery, payload).catch(function (error) {
            showToast(
              error instanceof Error
                ? error.message
                : "Unable to add this shared list right now.",
            );
          });
        }, 400);
      }
    } catch (error) {
      console.error("[Shared List Host] Failed to load shared list:", error);
      showToast(
        error instanceof Error
          ? error.message
          : "This shared list is unavailable.",
      );
    }
  }

  window.MrMapleSharedListHost = {
    openInvitationPreview: openInvitationPreview,
    prepareSharedList: prepareSharedList,
    joinPreparedSharedList: joinPreparedSharedList,
    openSharedListModal: openSharedListModal,
    openPublicPreview: openPublicPreview,
    syncPublicRouteFromLocation: syncPublicRouteFromLocation,
  };

  document.addEventListener("click", interceptPublicRouteClick);
  window.addEventListener("popstate", function () {
    void syncPublicRouteFromLocation({ closeMode: "history" });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrapFromUrl, {
      once: true,
    });
  } else {
    void bootstrapFromUrl();
  }
})();
